// src/utils/facebookPixel.js
// Facebook Pixel tracking helpers for BackPass

import ReactPixel from 'react-facebook-pixel';

/**
 * Helper to check if pixel is available and track event
 * Uses both ReactPixel and direct window.fbq as fallback
 */
const safeTrack = (eventName, eventData) => {
  console.log(`🔍 [PIXEL DEBUG] safeTrack called:`, { eventName, eventData });

  // Check if ReactPixel is available
  if (typeof ReactPixel !== 'undefined' && ReactPixel.track) {
    console.log(`✅ [PIXEL DEBUG] Using ReactPixel.track()`);
    try {
      ReactPixel.track(eventName, eventData);
      console.log(`✅ [PIXEL DEBUG] ReactPixel.track() executed successfully`);
    } catch (error) {
      console.error(`❌ [PIXEL DEBUG] ReactPixel.track() failed:`, error);
    }
  } else {
    console.warn(`⚠️ [PIXEL DEBUG] ReactPixel not available`);
  }

  // Also use direct window.fbq as fallback/redundancy
  if (typeof window !== 'undefined' && window.fbq) {
    console.log(`✅ [PIXEL DEBUG] Using window.fbq() as fallback`);
    try {
      window.fbq('track', eventName, eventData);
      console.log(`✅ [PIXEL DEBUG] window.fbq() executed successfully`);
    } catch (error) {
      console.error(`❌ [PIXEL DEBUG] window.fbq() failed:`, error);
    }
  } else {
    console.error(`❌ [PIXEL DEBUG] window.fbq not available! Pixel not initialized?`);
  }
};

/**
 * Track when a user views a route (ViewContent event)
 * @param {Object} route - Route object
 * @param {string} route.id - Route ID
 * @param {string} route.name - Route name
 * @param {number} route.storePrice - Store price in USD
 * @param {string} currency - Currency code (USD, EUR, GBP, etc.)
 * @param {number} convertedPrice - Price in converted currency
 */
export const trackViewRoute = (route, currency = 'USD', convertedPrice = null) => {
  console.log('🚀 [PIXEL DEBUG] trackViewRoute() CALLED', { route, currency, convertedPrice });

  try {
    const eventData = {
      content_name: route.name || route.id,
      content_ids: [route.id],
      content_type: 'product',
      value: convertedPrice || route.storePrice || 599, // Default to 599 if no price available
      currency: currency,
    };

    console.log('📊 [PIXEL DEBUG] ViewContent event data:', eventData);
    safeTrack('ViewContent', eventData);

    console.log('✅ [PIXEL DEBUG] trackViewRoute() completed');
  } catch (error) {
    console.error('❌ [PIXEL DEBUG] Error in trackViewRoute():', error);
    console.error('Error stack:', error.stack);
  }
};

/**
 * Track when a user initiates checkout (opens date selection)
 * @param {Object} route - Route object
 * @param {string} currency - Currency code
 * @param {number} estimatedPrice - Estimated price in selected currency
 */
export const trackInitiateCheckout = (route, currency = 'USD', estimatedPrice = null) => {
  console.log('🚀 [PIXEL DEBUG] trackInitiateCheckout() CALLED', { route, currency, estimatedPrice });

  try {
    const eventData = {
      content_name: route.name || route.id,
      content_ids: [route.id],
      content_type: 'product',
      value: estimatedPrice || route.storePrice || 599, // Default to 599 if no price available
      currency: currency,
      num_items: 1,
    };

    console.log('📊 [PIXEL DEBUG] InitiateCheckout event data:', eventData);
    safeTrack('InitiateCheckout', eventData);

    console.log('✅ [PIXEL DEBUG] trackInitiateCheckout() completed');
  } catch (error) {
    console.error('❌ [PIXEL DEBUG] Error in trackInitiateCheckout():', error);
    console.error('Error stack:', error.stack);
  }
};

/**
 * Track successful purchase (most important!)
 * @param {Object} params - Purchase parameters
 * @param {number} params.value - Total purchase value
 * @param {string} params.currency - Currency code (USD, EUR, GBP, etc.)
 * @param {string} params.routeName - Name of route purchased
 * @param {string} params.routeId - Route ID
 * @param {string} params.transactionId - Unique transaction/journey ID
 * @param {string} params.startDate - Journey start date
 * @param {string} params.endDate - Journey end date
 * @param {number} params.nights - Number of nights
 */
export const trackPurchase = ({
  value,
  currency,
  routeName,
  routeId,
  transactionId,
  startDate,
  endDate,
  nights,
}) => {
  console.log('🚀 [PIXEL DEBUG] trackPurchase() CALLED', {
    value,
    currency,
    routeName,
    routeId,
    transactionId,
    startDate,
    endDate,
    nights,
  });

  try {
    const eventData = {
      value: value,
      currency: currency,
      content_name: routeName,
      content_ids: [routeId],
      content_type: 'product',
      num_items: 1,
      // Additional data for better tracking
      transaction_id: transactionId,
      // Custom parameters (optional but useful)
      start_date: startDate,
      end_date: endDate,
      nights: nights,
    };

    console.log('💰 [PIXEL DEBUG] Purchase event data:', eventData);
    safeTrack('Purchase', eventData);

    console.log('🎉 [PIXEL DEBUG] trackPurchase() completed successfully!');
  } catch (error) {
    console.error('❌ [PIXEL DEBUG] Error in trackPurchase():', error);
    console.error('Error stack:', error.stack);
  }
};

/**
 * Track when a user adds payment info (optional, before purchase)
 * @param {Object} route - Route object
 * @param {string} currency - Currency code
 * @param {number} price - Price in selected currency
 */
export const trackAddPaymentInfo = (route, currency, price) => {
  console.log('🚀 [PIXEL DEBUG] trackAddPaymentInfo() CALLED', { route, currency, price });

  try {
    const eventData = {
      content_name: route.name || route.id,
      content_ids: [route.id],
      content_type: 'product',
      value: price,
      currency: currency,
    };

    console.log('📊 [PIXEL DEBUG] AddPaymentInfo event data:', eventData);
    safeTrack('AddPaymentInfo', eventData);

    console.log('✅ [PIXEL DEBUG] trackAddPaymentInfo() completed');
  } catch (error) {
    console.error('❌ [PIXEL DEBUG] Error in trackAddPaymentInfo():', error);
    console.error('Error stack:', error.stack);
  }
};

const facebookPixelTracking = {
  trackViewRoute,
  trackInitiateCheckout,
  trackPurchase,
  trackAddPaymentInfo,
};

export default facebookPixelTracking;
